import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, Loader2, CheckCircle2, MapPin, Clock } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function PublicMissionBooking() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  const load = () => api.get(`/public/mission-booking/${token}`).then((r) => {
    setInfo(r.data);
    setState(r.data.booked_by_tenant ? "done" : "ready");
  }).catch(() => setState("invalid"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const book = async (slot) => {
    setBusy(true); setChosen(slot);
    try { await api.post(`/public/mission-booking/${token}`, { date: slot.date, time: slot.time || "" }); setState("done"); setInfo((i) => ({ ...i, scheduled_at: slot.date, scheduled_time: slot.time })); toast.success("Créneau confirmé"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); load(); } finally { setBusy(false); }
  };

  const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };

  return (
    <div className="min-h-screen bg-brand-softer flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl border border-brand-soft p-8 w-full max-w-md" data-testid="public-booking-page">
        <div className="w-12 h-12 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><CalendarClock className="w-6 h-6" /></div>
        {state === "loading" && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand-green/40" /></div>}
        {state === "invalid" && <p className="text-sm text-brand-green/60 mt-4" data-testid="booking-invalid">Ce lien de réservation est invalide ou a expiré.</p>}
        {state === "done" && info && (
          <div className="mt-4 text-center" data-testid="booking-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="font-semibold text-brand-greenDeep mt-3">Rendez-vous confirmé !</p>
            <p className="text-sm text-brand-green/60 mt-1">{info.mission_label} · {info.property_name}</p>
            <p className="text-sm text-brand-greenDeep font-medium mt-2 flex items-center justify-center gap-1"><Clock className="w-4 h-4" />{fmtDate(info.scheduled_at)}{info.scheduled_time ? ` à ${info.scheduled_time}` : ""}</p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/mission-booking/${token}/ics`} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="booking-ics-download"><CalendarClock className="w-4 h-4" /> Ajouter à mon agenda (.ics)</a>
            <p className="text-xs text-brand-green/45 mt-3">Le référent et le propriétaire ont été prévenus. Vous recevrez un rappel avant le rendez-vous.</p>
          </div>
        )}
        {state === "ready" && info && (
          <>
            <h1 className="font-display text-xl font-semibold text-brand-greenDeep mt-4">Choisissez votre créneau</h1>
            <p className="text-sm text-brand-green/60 mt-1">{info.mission_label} · {info.property_name}</p>
            {info.referent_name && <p className="text-xs text-brand-green/45 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />Référent : {info.referent_name}</p>}
            <div className="mt-5 space-y-2" data-testid="booking-slots">
              {(info.proposed_slots || []).length === 0 && <p className="text-sm text-brand-green/50">Aucun créneau proposé pour le moment.</p>}
              {(info.proposed_slots || []).map((s, i) => (
                <button key={i} onClick={() => book(s)} disabled={busy} className="w-full text-left px-4 py-3 rounded-2xl border border-brand-soft hover:border-brand-greenDeep hover:bg-brand-lime/10 transition-colors disabled:opacity-60 flex items-center justify-between" data-testid={`booking-choose-${i}`}>
                  <span className="text-sm font-semibold text-brand-greenDeep capitalize flex items-center gap-2"><Clock className="w-4 h-4 text-brand-green/40" />{fmtDate(s.date)}{s.time ? ` · ${s.time}` : ""}</span>
                  {busy && chosen === s ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-xs text-brand-greenDeep font-semibold">Choisir</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
